import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import queryString from "query-string";
import add from "date-fns/add";
import isToday from "date-fns/isToday";
import { Box, Grid, Button, IconButton, Hidden } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import PaginationItem from "@material-ui/lab/PaginationItem";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircle";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { NotificationSnackbar } from "../components/common/NotificationSnackbar";
import { BatchTable } from "../components/table/BatchTable";
import LocalizedDatePicker from "../components/common/LocalizedDatePicker";
import { Heading } from "../components/common/Heading";
import { UserGroup } from "../model/user";
import { Unauthorized } from "../pages/Unauthorized";
import { fetchBatches } from "../state/findBatches";
import { LoadingState } from "../state/loadingState";
import { clearMessage } from "../state/messages";
import { RootState, useAppDispatch } from "../reduxStore";

const ListBatches = (): JSX.Element => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userGroups = useSelector((state: RootState) => state.userGroups.userGroups);
  const isAuthorized = userGroups?.includes(UserGroup.WAREHOUSE_PERSONNEL_GROUP);

  const batches = useSelector((state: RootState) => state.batches.find.batches);
  const count = useSelector((state: RootState) => state.batches.find.count);
  const batchesLoading = useSelector((state: RootState) => state.batches.find.loading);

  const messages = useSelector((state: RootState) => state.messages);
  const { page = 1 }: any = queryString.parse(location.search);
  const pageSize = 10;

  const [selectedDate, handleDateChange] = useState<Date>(new Date());

  useEffect(() => {
    if (!isAuthorized) return;
    dispatch(fetchBatches({ page: parseInt(page), pageSize, filters: { date: selectedDate || new Date() } }));
  }, [page, isAuthorized, selectedDate, dispatch]);

  if (!isAuthorized) return <Unauthorized />;

  return (
    <Box>
      <Grid container direction="column" spacing={2}>
        <Grid item container direction="row" justify="space-between">
          <Grid item xs={6}>
            <Heading variant="h2">Saapumiserät</Heading>
          </Grid>
          <Grid item xs={6}>
            <Box display="flex" justifyContent="flex-end" py={2}>
              <Hidden only="xs">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => {
                    history.push("/add");
                  }}
                >
                  Lisää uusi
                </Button>
              </Hidden>
              <Hidden smUp>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    history.push("/add");
                  }}
                >
                  <AddCircleOutlineIcon />
                </Button>
              </Hidden>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs>
          <Box display="flex" justifyContent="center">
            <IconButton size="medium" onClick={() => handleDateChange(add(selectedDate, { days: -1 }))}>
              <KeyboardArrowLeftIcon />
            </IconButton>
            <LocalizedDatePicker
              value={selectedDate}
              autoOk
              disableFuture
              variant="inline"
              onChange={(value: Date | null) => handleDateChange(value || new Date())}
            />
            <IconButton
              size="medium"
              onClick={() => handleDateChange(add(selectedDate, { days: 1 }))}
              disabled={isToday(selectedDate)}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <BatchTable data={batches} loading={batchesLoading === LoadingState.Pending} />
        </Grid>
        <Grid item>
          <Box p={2} display="flex" flexDirection="column" alignItems="center">
            <Pagination
              page={parseInt(page)}
              count={Math.max(1, Math.ceil(count / pageSize))}
              color="primary"
              disabled={count === 0}
              renderItem={(item: any) => (
                <PaginationItem component={Link} to={`/list${item.page === 1 ? "" : `?page=${item.page}`}`} {...item} />
              )}
            />
          </Box>
        </Grid>
      </Grid>

      <NotificationSnackbar
        text={messages.success}
        severity="success"
        onClosed={() => {
          dispatch(clearMessage());
        }}
      />
      <NotificationSnackbar
        text={messages.error}
        severity="error"
        onClosed={() => {
          dispatch(clearMessage());
        }}
      />
    </Box>
  );
};

export default ListBatches;
